import { useCallback, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormHelperText from '@material-ui/core/FormHelperText';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';

import { Form, Formik, Field, FormikHelpers, ErrorMessage } from 'formik';
import { TextField } from 'formik-material-ui';
import * as Yup from 'yup';
import clsx from 'clsx';

import { ReserveFeedbackModel } from '../../../services/models';
import { ReservationService } from '../../../services';
import { formatError, snackbarUtils } from '../../../utils';

import ThanksForm from '../../ThanksForm';
import { CursingEmoji, HappyEmoji, SadEmoji, StarEyesEmoji } from '../../../assets/icons/emojis';
import useCommonStyles, { blueButtonProps } from '../../../styles/common';

interface Props {
  isOpen: boolean;
  handleClose: () => void;
  reservationId: number;
}

interface FormValues {
  rate: number | null;
  comment: string;
}

const Values: FormValues = {
  rate: null,
  comment: '',
}

const validationSchema = Yup.object().shape({
  rate: Yup.number().required('Escolha uma avaliação (emoji)'),
  comment: Yup.string(),
});

const makeEmojiOptions = () => [
  {
    id: 1,
    title: 'Péssimo',
    emoji: CursingEmoji,
    altEmoji: 'emoji-xingando'
  },
  {
    id: 2,
    title: 'Ruim',
    emoji: SadEmoji,
    altEmoji: 'emoji-triste',
  },
  {
    id: 3,
    title: 'Bom',
    emoji: HappyEmoji,
    altEmoji: 'emoji-feliz'
  },
  {
    id: 4,
    title: 'Ótimo',
    emoji: StarEyesEmoji,
    altEmoji: 'emoji-olhos-de-estrela'
  },
];

const FormDialog = (props: Props) => {
  const { isOpen, handleClose, reservationId } = props;
  const classes = useStyles();
  const commonClasses = useCommonStyles();

  const [isFinished, setIsFinished] = useState(false);
  const [options] = useState(makeEmojiOptions());

  const characterLimit = 280;

  const handleAppFeedback = useCallback(async () => {
    const { data } = await ReservationService.finisheds();
    const finishedsCount = data.length;
    if (finishedsCount === 2) localStorage.setItem('surveyIsAnswered', 'false');
    else if (finishedsCount % 5 === 0) localStorage.setItem('surveyIsAnswered', 'false');
    else return;
  }, [])

  const handleFormik = useCallback(async (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    try {
      // console.log(values);
      const data: ReserveFeedbackModel = {
        reservationId: reservationId,
        feedbackComment: values.comment,
        feedbackRate: values.rate!,
      }
      await ReservationService.feedback(data);
      handleAppFeedback();
      setIsFinished(true);
    } catch (error) {
      console.log(error.message);
      snackbarUtils.error(formatError(error));
    }
  }, [handleAppFeedback, reservationId]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      className={classes.dialog}
      aria-labelledby="simple-dialog-title"
    >
      {!isFinished ? (
        <Formik initialValues={Values} validationSchema={validationSchema} onSubmit={handleFormik}>
          {({ isSubmitting, setFieldValue, touched, errors, values }) => (
            <Form>
              <DialogTitle
                id="simple-dialog-title"
                className={classes.title}
              >
                Como foi o seu dia de trabalho hoje?
              </DialogTitle>

              <DialogContent>
                <DialogContentText className={classes.description}>
                  Se preferir deixe um comentário.
                </DialogContentText>

                <Grid container item xs={12} spacing={2} className={classes.rateContainer}>
                  {options.map(({ id, title, emoji, altEmoji }) => (
                    <Grid key={id} item xs={3}>
                      <Tooltip title={title} open={values.rate === id} arrow>
                        <IconButton
                          data-cy={`${title}-rate`}
                          onClick={() => setFieldValue('rate', id)}
                          className={clsx({
                            [classes.rateButton]: true,
                            'selected': values.rate === id
                          })}
                        >
                          <img src={emoji} alt={altEmoji} draggable="false" />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  ))}
                </Grid>
                <FormHelperText><ErrorMessage name="desk" /></FormHelperText>
                <Field
                  id="comment"
                  name="comment"
                  margin="dense"
                  label="Comentário"
                  placeholder="Quer deixar um comentário?"
                  component={TextField}
                  error={touched.comment && Boolean(errors.comment)}
                  helperText={`${values.comment.length}/${characterLimit}`}
                  InputLabelProps={{
                    classes: {
                      focused: classes.textFieldLabel,
                    },
                  }}
                  InputProps={{
                    classes: {
                      notchedOutline: classes.textFieldOutline,
                    },
                  }}
                  inputProps={{
                    maxLength: characterLimit
                  }}
                  fullWidth
                  multiline
                  rowsMin={4}
                />
              </DialogContent>

              <DialogActions className={classes.actions}>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  className={commonClasses.outlinedButton}
                >
                  Cancelar
                </Button>
                <Button
                  id="feedback-button"
                  type="submit"
                  startIcon={isSubmitting ? <CircularProgress size="1rem" /> : null}
                  disabled={isSubmitting || !values.rate}
                  className={commonClasses.blueButton}
                  {...blueButtonProps}
                >
                  Enviar
                </Button>
              </DialogActions>
            </Form>
          )
          }
        </Formik >
      ) : (
        <ThanksForm reservationId={reservationId} handleClose={handleClose} />
      )}
    </Dialog >
  );
}

export default FormDialog;

const useStyles = makeStyles<Theme>((theme) =>
  createStyles({
    dialog: {
      minWidth: 200,
      textAlign: 'center',

      '& .MuiBackdrop-root': {
        backdropFilter: 'blur(40px)'
      },
      '& div > p': {
        color: theme.palette.text.primary,
      }
    },
    title: {
      '& h2': {
        color: theme.palette.primary.main,
        fontSize: theme.typography.pxToRem(22),
        fontWeight: theme.typography.fontWeightBold,
      }
    },

    rateContainer: {
      margin: theme.spacing(3, 0, 5),
    },
    rateButton: {
      border: '4px solid #F2F2F2',

      '&.selected': {
        '& img': {
          transform: 'scale(2)'
        }
      },
      '& img': {
        width: '100%',
        maxWidth: 40,
        height: 'auto',
        transition: 'transform .2s',
      }
    },

    actions: {
      marginTop: theme.spacing(3),
      alignItems: 'flex-end',
      justifyContent: 'center',

      '& > #feedback-button': {
        marginTop: theme.spacing(2),
        [theme.breakpoints.up('sm')]: {
          marginTop: 'unset',
        }
      },

      [theme.breakpoints.up('sm')]: {
        flexDirection: 'row',
        alignItems: 'flex-end'
      }
    },
  })
);